/**
 * Platform-admin console client. The backend returns 404 for anyone who is
 * not a platform admin, so usePlatformAdmin() is false for them.
 */
import { useEffect, useState } from 'react';
import api from './api';
import type { FeatureKey, LimitKey } from './billing';

export interface PlatformOrgRow {
  id: number;
  name: string;
  slug: string;
  ownerEmail: string | null;
  createdAt: string;
  plan: string;
  planStatus: string | null;
  effectivePlan: string;
  effectiveStatus: string | null;
  trialEndsAt: string | null;
  trialDaysLeft: number | null;
  planExpiresAt: string | null;
  suspendedAt: string | null;
  hasOverrides: boolean;
  limits: Record<LimitKey, number | null>;
  // aiCreditsPerMonth is null until the aiUsage table exists.
  usage: { venues: number; gameNightsPerMonth: number; aiCreditsPerMonth: number | null };
  lastGameNightAt: string | null;
  lastActivityAt: string | null;
}

export interface PlatformSummary {
  orgCount: number;
  byPlan: Record<string, number>;
  byStatus: Record<string, number>;
  trialsEndingSoon: { id: number; name: string; trialEndsAt: string; trialDaysLeft: number }[];
  estimatedMrrUsd: number;
  newOrgsThisMonth: number;
}

export interface PlatformOrgList {
  billingEnabled: boolean;
  summary: PlatformSummary;
  orgs: PlatformOrgRow[];
}

export interface AuditEntry {
  id: number;
  createdAt: string;
  actorEmail: string;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string;
}

export interface PlatformOrgDetail {
  billingEnabled: boolean;
  org: PlatformOrgRow;
  billing: {
    stripeCustomerId: string | null;
    stripeSubscriptionId: string | null;
    links: { customer: string | null; subscription: string | null };
  };
  overrides: { features: Partial<Record<FeatureKey, boolean>>; limits: Partial<Record<LimitKey, number | null>> };
  suspendedReason: string | null;
  features: Record<FeatureKey, boolean>;
  plans: { key: string; name: string }[];
  featureKeys: FeatureKey[];
  limitKeys: LimitKey[];
  members: { id: number; email: string; name: string | null; role: string; createdAt: string; lastSeenAt: string | null; lastLoginAt: string | null }[];
  venues: { id: number; name: string; createdAt: string }[];
  recentNights: { id: string; name: string | null; date: string; status: string; venue: { name: string } | null; quiz: { name: string } | null }[];
  audit: AuditEntry[];
}

export async function fetchPlatformOrgs(): Promise<PlatformOrgList> {
  const res = await api.get('/api/platform/orgs');
  return res.data.data;
}

export async function fetchPlatformOrg(id: number | string): Promise<PlatformOrgDetail> {
  const res = await api.get(`/api/platform/orgs/${id}`);
  return res.data.data;
}

export type PlatformAction =
  | { kind: 'plan'; body: { plan: string; expiresAt?: string | null } }
  | { kind: 'trial'; body: { mode: 'extend' | 'reset'; days?: number } }
  | { kind: 'overrides'; body: { features: Partial<Record<FeatureKey, boolean>>; limits: Partial<Record<LimitKey, number | null>> } }
  | { kind: 'suspend'; body: Record<string, never> }
  | { kind: 'unsuspend'; body: Record<string, never> };

// Run one write action. Returns the fresh org detail.
export async function runPlatformAction(id: number, action: PlatformAction, reason: string): Promise<PlatformOrgDetail> {
  const body = { ...action.body, reason };
  const path = `/api/platform/orgs/${id}/${action.kind}`;
  const res = action.kind === 'overrides' ? await api.put(path, body) : await api.post(path, body);
  return res.data.data;
}

// True once the backend confirms that the caller is a platform admin.
export function usePlatformAdmin(): boolean {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let mounted = true;
    api.get('/api/platform/me')
      .then(() => { if (mounted) setIsAdmin(true); })
      .catch(() => { if (mounted) setIsAdmin(false); });
    return () => { mounted = false; };
  }, []);
  return isAdmin;
}

export interface OrgFilter {
  q: string;
  plan: string; // '' = any
  status: string; // '' = any
  trialSoon: boolean; // trial ends in 7 days or fewer
}

export type OrgSortKey = 'name' | 'createdAt' | 'plan' | 'trialEndsAt' | 'lastActivityAt' | 'lastGameNightAt' | 'venues';

export function filterOrgs(rows: PlatformOrgRow[], f: OrgFilter): PlatformOrgRow[] {
  const q = f.q.trim().toLowerCase();
  return rows.filter((r) => {
    if (q && !`${r.name} ${r.slug} ${r.ownerEmail ?? ''}`.toLowerCase().includes(q)) return false;
    if (f.plan && r.effectivePlan !== f.plan) return false;
    if (f.status && (r.effectiveStatus ?? 'none') !== f.status) return false;
    if (f.trialSoon && !(r.effectiveStatus === 'trialing' && (r.trialDaysLeft ?? 99) <= 7)) return false;
    return true;
  });
}

function sortValue(r: PlatformOrgRow, key: OrgSortKey): string | number {
  switch (key) {
    case 'name': return r.name.toLowerCase();
    case 'plan': return r.effectivePlan;
    case 'venues': return r.usage.venues;
    case 'createdAt': return Date.parse(r.createdAt) || 0;
    case 'trialEndsAt': return r.trialEndsAt ? Date.parse(r.trialEndsAt) : Number.MAX_SAFE_INTEGER;
    case 'lastActivityAt': return r.lastActivityAt ? Date.parse(r.lastActivityAt) : 0;
    case 'lastGameNightAt': return r.lastGameNightAt ? Date.parse(r.lastGameNightAt) : 0;
    default: return 0;
  }
}

export function sortOrgs(rows: PlatformOrgRow[], key: OrgSortKey, dir: 'asc' | 'desc'): PlatformOrgRow[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = sortValue(a, key);
    const vb = sortValue(b, key);
    if (va < vb) return -1 * sign;
    if (va > vb) return 1 * sign;
    return a.id - b.id;
  });
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}
